import { useTypedDispatch } from "App/Store";
import { SelectOptions } from "core/models/Settings";
import { Brand } from "core/models/Brand";
import { convertFileToBase64 } from "core/utils/Image";
import { maxLength } from "core/utils/Normalizer";
import { useCallback, useEffect, useState } from "react";
import {
  Field,
  formValueSelector,
  InjectedFormProps,
  reduxForm,
} from "redux-form";
import { useSelector } from "react-redux";
import { hideLoader, showLoader } from "redux/action/loaderAction";
import { Button, Form } from "rsuite";
import InputWithLabel from "shared/components/redux-form/InputWithLabel";
import Select from "shared/components/redux-form/Select";
import MultiSelect from "shared/components/redux-form/MultiSelect";
import TextArea from "shared/components/redux-form/TextArea";
import UploadWithPreview from "shared/components/UploadWithPreview";
import Arrow from "shared/Icons/Arrow";
import clsx from "clsx";
import { useHistory } from "react-router-dom";
import { getBrands } from "core/services/Brand";
import { getCategories } from "core/services/Category";
import { getCountries } from "core/services/Common";
import { getGames } from "core/services/Game";
import { PLATFORM_OPTIONS } from "core/constants/Store";
import validate from "./validate";

interface AddBrandFormProps {
  onAddBrand(brandData: any): void;
  isEditMode: boolean;
  brand?: Brand | null;
}

const booleanOptions: SelectOptions[] = [
  { label: "Yes", value: true as any },
  { label: "No", value: false as any },
];

// JSON array fields (categoryIds / regions / platforms) may come back as an
// array or (for legacy rows) a JSON string.
function parseJsonArray<T = string>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (typeof value === "string" && value) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

type FinalProps = InjectedFormProps<{}, AddBrandFormProps> & AddBrandFormProps;

const AddBrandForm = ({
  handleSubmit,
  onAddBrand,
  initialize,
  isEditMode,
  brand,
  reset,
}: FinalProps) => {
  const [logo, setLogo] = useState("");
  const [banner, setBanner] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<SelectOptions[]>([]);
  const [regionOptions, setRegionOptions] = useState<SelectOptions[]>([]);
  const [gameOptions, setGameOptions] = useState<SelectOptions[]>([]);
  const [gameBrandOptions, setGameBrandOptions] = useState<SelectOptions[]>([]);
  const [giftCardBrandOptions, setGiftCardBrandOptions] = useState<
    SelectOptions[]
  >([]);

  const dispatch = useTypedDispatch();
  const history = useHistory();
  const selector = formValueSelector("add-brand");
  const isGame = useSelector((state) => selector(state, "isGame"));

  const loadOptions = useCallback(async () => {
    const [categoryRes, countryRes, gameRes, brandRes] = await Promise.all([
      getCategories(),
      getCountries(),
      getGames({ page: 0, size: 500 }),
      getBrands(),
    ]);
    if (categoryRes?.status && categoryRes?.data?.categories?.length) {
      setCategoryOptions(
        categoryRes.data.categories.map((c: any) => ({
          label: c.name,
          value: c.id,
        }))
      );
    }
    if (countryRes?.status && countryRes?.data?.countries?.length) {
      setRegionOptions([
        ...countryRes.data.countries.map((c: any) => ({
          label: c.label,
          value: c.label,
        })),
        { label: "Global", value: "Global" },
      ]);
    }
    if (gameRes?.status && gameRes?.data?.games?.length) {
      setGameOptions(
        gameRes.data.games.map((g: any) => ({ label: g.name, value: g.id }))
      );
    }
    // Split every brand by isGame so each side of the link only offers the
    // opposite kind: a gift-card brand picks games, a game picks gift cards.
    if (brandRes?.status && brandRes?.data?.brands?.length) {
      const toOption = (b: Brand) => ({ label: b.name, value: b.id });
      setGameBrandOptions(
        brandRes.data.brands.filter((b: Brand) => b.isGame).map(toOption)
      );
      setGiftCardBrandOptions(
        brandRes.data.brands.filter((b: Brand) => !b.isGame).map(toOption)
      );
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    if (!isEditMode) {
      reset();
      initialize({ isGame: false, isPopular: false, isPublished: true });
      setLogo("");
      setBanner("");
      setLogoFile(null);
      setBannerFile(null);
    } else if (brand) {
      initialize({
        name: brand.name,
        isGame: brand.isGame,
        gameId: brand.gameId ?? undefined,
        categoryIds: parseJsonArray<number>(brand.categoryIds),
        supportedGameIds: (brand.supportedGames ?? []).map((b) => b.id),
        acceptedGiftCardIds: (brand.acceptedGiftCards ?? []).map((b) => b.id),
        developer: brand.developer ?? "",
        regions: parseJsonArray(brand.regions),
        platforms: parseJsonArray(brand.platforms),
        videoUrl: brand.videoUrl ?? "",
        about: brand.about ?? "",
        howToInstall: brand.howToInstall ?? "",
        isPopular: brand.isPopular,
        isPublished: brand.isPublished,
      });
      setLogo(brand.logo ?? "");
      setBanner(brand.bannerImage ?? "");
      setLogoFile(null);
      setBannerFile(null);
    }
  }, [reset, isEditMode, brand, initialize]);

  async function handleUpload(files: File[], type: "logo" | "banner") {
    dispatch(showLoader());
    if (files.length) {
      const file = files[0];
      const imageUrl = await convertFileToBase64(file);
      if (imageUrl) {
        if (type === "logo") {
          setLogo(imageUrl);
          setLogoFile(file);
        } else {
          setBanner(imageUrl);
          setBannerFile(file);
        }
      }
    }
    dispatch(hideLoader());
  }

  function handleForm(formData: any) {
    const formFields: any = { ...formData };
    formFields.gameId = formFields.isGame ? formFields.gameId ?? "" : "";
    // Only one side of the brand<->brand link is editable at a time. Send the
    // hidden side as empty rather than omitting it, so flipping isGame clears
    // links that no longer make sense instead of leaving them orphaned.
    if (formFields.isGame) {
      formFields.acceptedGiftCardIds = formFields.acceptedGiftCardIds ?? [];
      formFields.supportedGameIds = [];
    } else {
      formFields.supportedGameIds = formFields.supportedGameIds ?? [];
      formFields.acceptedGiftCardIds = [];
    }
    if (logoFile) formFields.logo = logoFile;
    if (bannerFile) formFields.bannerImage = bannerFile;
    onAddBrand(formFields);
  }

  function handleRedirection() {
    history.goBack();
  }

  return (
    <Form fluid onSubmit={handleSubmit(handleForm)}>
      <div className="add-tournament-form">
        <div className="add-tournament-form-title-wrapper">
          <span className="form-back-btn" onClick={handleRedirection}>
            <Arrow fontSize={32} />
          </span>
          <h4>{isEditMode ? "Edit Brand" : "Create Brand"}</h4>
        </div>

        <div className="section-wrapper">
          <div className="section-title">Upload</div>
          <div className="tournaments-uploads-wrapper">
            <div className="tournament-icon">
              <UploadWithPreview
                onFileChange={(file) => handleUpload(file, "logo")}
                onRemove={() => setLogo("")}
                label="Brand Logo"
                placeholder="Add Brand Logo"
                accept="image/*"
                dimension="[1024px x 1024px]"
                cropResolution={{ width: 1024, height: 1024 }}
                value={logo}
              />
            </div>
            <div className="tournament-banner">
              <UploadWithPreview
                onFileChange={(file) => handleUpload(file, "banner")}
                onRemove={() => setBanner("")}
                label="Brand Banner"
                placeholder="Add Brand Banner"
                accept="image/*"
                value={banner}
                dimension="[1440px x 420px]"
              />
            </div>
          </div>

          <div className="section-title">Basic Info</div>
          <div className={clsx("tournament-grid-form-wrapper", "spacing")}>
            <Field
              name="name"
              component={InputWithLabel}
              placeholder="Enter Brand Name"
              label="Brand Name"
              normalize={(value: string) => maxLength(value, 80)}
            />
            <Field
              name="isGame"
              component={Select}
              placeholder="Is this a game?"
              label="Is Game"
              options={booleanOptions}
            />
            {isGame && (
              <Field
                searchable
                cleanable
                name="gameId"
                component={Select}
                placeholder="Select Game (Optional)"
                label="Linked Arena Game (Optional)"
                options={gameOptions}
              />
            )}
            <Field
              cleanable
              searchable
              name="categoryIds"
              component={MultiSelect}
              placeholder="Select Categories"
              label="Categories"
              options={categoryOptions}
            />
            {isGame ? (
              <Field
                cleanable
                searchable
                name="acceptedGiftCardIds"
                component={MultiSelect}
                placeholder="Select Gift Card Brands"
                label="Recharge With (Gift Card Brands)"
                options={giftCardBrandOptions}
              />
            ) : (
              <Field
                cleanable
                searchable
                name="supportedGameIds"
                component={MultiSelect}
                placeholder="Select Games"
                label="Supported Games"
                options={gameBrandOptions}
              />
            )}
            <Field
              name="developer"
              component={InputWithLabel}
              placeholder="Enter Developer / Publisher"
              label="Developer"
              normalize={(value: string) => maxLength(value, 80)}
            />
            <Field
              searchable
              cleanable
              name="regions"
              component={MultiSelect}
              placeholder="Select Regions (or Global)"
              label="Regions"
              options={regionOptions}
            />
            <Field
              searchable
              cleanable
              name="platforms"
              component={MultiSelect}
              placeholder="Select Platforms"
              label="Platforms"
              options={PLATFORM_OPTIONS}
            />
            <Field
              name="videoUrl"
              component={InputWithLabel}
              placeholder="Enter Video URL"
              label="Video URL"
              normalize={(value: string) => maxLength(value, 255)}
            />
            <Field
              name="isPopular"
              component={Select}
              placeholder="Is Popular"
              label="Is Popular"
              options={booleanOptions}
            />
            <Field
              name="isPublished"
              component={Select}
              placeholder="Is Published"
              label="Is Published"
              options={booleanOptions}
            />
          </div>

          <div className={clsx("spacing")}>
            <Field
              name="about"
              component={TextArea}
              placeholder="About this brand / game"
              label="About"
              rows={5}
            />
            <Field
              name="howToInstall"
              component={TextArea}
              placeholder="How to download and install"
              label="How To Install"
              rows={5}
            />
          </div>

          <div className={clsx("create-tourney-btn", "spacing")}>
            <Button appearance="primary" type="submit">
              {isEditMode ? "Update Brand" : "Create Brand"}
            </Button>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default reduxForm<{}, AddBrandFormProps>({
  form: "add-brand",
  validate: (values) => validate(values),
})(AddBrandForm);
